<script lang="ts" setup>
import { onMounted, ref, set, watch } from 'vue'
import { useConfigStore, useLoadingStore, useToastStore } from '@/stores'
import { AD_TYPE } from '@/constants'
import { KS } from '@/kuaishou'
import { $log } from '@/helpers'
import { track } from '@/track'

const props = defineProps<{
  value: boolean
  contentId: string
  chapterId: string
}>()
const emit = defineEmits<{
  (event: 'input', value: boolean): void
}>()

const { $showLoading, $hideLoading } = useLoadingStore()
const { $toast } = useToastStore()

const configStore = useConfigStore()

const adVideoPlayer = ref(false)
const adVideoData = ref<{ sessionId: string; src: string; poster: string; isDownload: boolean; [key: string]: any }>({ sessionId: '', src: '', poster: '', isDownload: false })
const adImagePlayer = ref(false)
const adImageData = ref({ caption: '', coverUrl: '', h5Data: {} as any, detailSessionId: '' })
onMounted(async () => {
  if (configStore.$state.displayForm === AD_TYPE.VIDEO)
    await fetchAdVideo()
  else if (configStore.$state.displayForm === AD_TYPE.IMAGE)
    await fetchAdImage()
})

async function fetchAdVideo() {
  $showLoading()
  try {
    const result = await KS.getAdVideo({ type: 4 })
    $log('Component: BizAdFull', '#getAdVideo', { result })
    const { sessionId, adData, error_msg } = result

    if (!adData) {
      $toast(error_msg)
      emit('input', false)
      return
    }

    adVideoPlayer.value = true
    adVideoData.value = {
      sessionId,
      src: adData.videoUrl,
      poster: adData.coverImage,
      caption: adData.caption,
      isDownload: adData.isDownload,
      actionBar: adData.actionBar,
      actionBarColor: adData.actionBarColor,
    }
  }
  finally {
    $hideLoading()
  }
}

async function fetchAdImage() {
  $showLoading()
  try {
    const result = await KS.getAdImage()
    $log('Component: BizAdFull', '#getAdImage', { result })

    const { detailSessionId, adData, error_msg } = result

    if (!adData) {
      $toast(error_msg)
      emit('input', false)
      return
    }

    adImagePlayer.value = true
    let h5Data: any = {}
    try {
      h5Data = JSON.parse(adData.h5Data)
    }
    catch (error) {
      $log('component: BizAdFull', '#getAdImage ~> parse H5Data', { error })
    }
    adImageData.value.coverUrl = adData.coverUrl.replace('.kpg', '.png') // replace extension
    adImageData.value.caption = adData.caption
    adImageData.value.detailSessionId = detailSessionId
    set(adImageData.value, 'h5Data', h5Data)
  }
  finally {
    $hideLoading()
  }
}

function onErrorAdVideo() {
  emit('input', false)
}
// Note: Auto refresh
const adVideoPlayerRef = ref()
async function onEndAdVideo() {
  adVideoData.value.src = ''
  await fetchAdVideo()
  track('insertAd', {
    contentId: props.contentId,
    chapterId: props.chapterId,
    sessionId: adVideoPlayer.value ? adVideoData.value.sessionId : adImageData.value.detailSessionId,
    adType: adVideoPlayer.value ? 'video' : 'image',
    interval: configStore.$state.displayIntervals,
  })
  const $video = adVideoPlayerRef.value?.videoRef as HTMLVideoElement
  if ($video) {
    try {
      $video.currentTime = 0
      $video.play()
    }
    catch {}
  }
}

watch(() => [adVideoPlayer.value, adImagePlayer.value], () => {
  if (!adVideoPlayer.value && !adImagePlayer.value)
    emit('input', false)
})
</script>

<template>
  <portal to="root" :order="11">
    <template v-if="adVideoPlayer || adImagePlayer">
      <BizTrackShow
        name="insertAd" type="page"
        :extra="{
          contentId,
          chapterId,
          sessionId: adVideoPlayer ? adVideoData.sessionId : adImageData.detailSessionId,
          adType: adVideoPlayer ? 'video' : 'image',
          interval: configStore.$state.displayIntervals,
        }"
      >
        <BizAdVideoPlayer
          v-if="adVideoPlayer" ref="adVideoPlayerRef"
          v-model="adVideoPlayer" :content-id="contentId" :chapter-id="chapterId"
          :session-id="adVideoData.sessionId" :receive-coin="false"
          :src="adVideoData.src" :poster="adVideoData.poster" :caption="adVideoData.caption"
          :is-download="adVideoData.isDownload" :action-bar="adVideoData.actionBar" :action-bar-color="adVideoData.actionBarColor"
          @error="onErrorAdVideo" @end="onEndAdVideo"
        />
        <BizAdImagePlayer
          v-if="adImagePlayer"
          v-model="adImagePlayer" :content-id="contentId" :chapter-id="chapterId"
          :detail-session-id="adImageData.detailSessionId" :caption="adImageData.caption"
          :cover-url="adImageData.coverUrl" :h5-data="adImageData.h5Data"
        />
      </BizTrackShow>
    </template>
  </portal>
</template>
